/**
 * The content area every layout places somewhere: the open screen, rendered
 * exactly as it renders today (the host's `renderObject`).
 *
 * The KEPT-ALIVE editor instances (`EditorSessionHosts`, below) are mounted by
 * `RunNavShell` itself, OUTSIDE the layout: switching layout replaces the
 * layout's whole subtree, and an editor mounted inside it would lose what was
 * typed.
 *
 * A session instance is the same `renderObject(item)` under a 'session'
 * editor context: its TxnScreen opens the session's record, shows only its
 * record editor (portaled over whatever screen is on display) and keeps that
 * editor mounted while minimised — so nothing typed is re-implemented, it is
 * simply never unmounted. The instances sit in a hidden container; only the
 * shown session's editor is visible.
 */
import { useMemo, type CSSProperties, type ReactNode } from 'react'
import { ObjectChromeProvider } from '../../objectChrome'
import { ShellEditorProvider, type ShellEditorMain, type ShellEditorSession } from './editorContext'
import type { EditorSession } from './editorSessions'
import type { RenderObjectContext, RunNavItem } from './types'
import type { ShellNavModel } from './useShellNav'

export interface ShellFrameProps {
  model: ShellNavModel
  renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
  /** Drawn when no screen is open. */
  empty?: ReactNode
  padding?: string
  style?: CSSProperties
}

export function ShellFrame({ model, renderObject, empty, padding = '0.5rem 1rem 1rem', style }: ShellFrameProps) {
  const current = model.current
  return (
    <>
      {current ? (
        <div data-tw-open={current.obj_name} data-tw-open-type={current.obj_type} data-tw-open-current=""
          style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: model.fullScreen ? '0.5rem 0.75rem' : padding, ...style }}>
          <MainScreen model={model} item={current} renderObject={renderObject} />
        </div>
      ) : empty}
    </>
  )
}

/**
 * One kept-alive instance per open editor. `display: none` hides their own
 * (unused) lists; their record editors portal to <body> and decide their own
 * visibility. Mounted once by RunNavShell, never inside a layout.
 */
export function EditorSessionHosts({ model, renderObject }: {
  model: ShellNavModel; renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
}) {
  if (!model.editorsEnabled) return null
  return (
    <div aria-hidden style={{ display: 'none' }} data-tw-shell-editors="">
      {model.editors.filter((s) => s.mounted).map((s) => (
        <SessionHost key={s.id} model={model} session={s} renderObject={renderObject} />
      ))}
    </div>
  )
}

/**
 * One open screen, as the URL's current screen or (`background`) as another
 * desktop window. A layout with several windows renders one per open screen;
 * every other layout uses ShellFrame, which renders the current one.
 */
export function ScreenView({ model, item, renderObject, background = false }: {
  model: ShellNavModel; item: RunNavItem
  renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
  background?: boolean
}) {
  return <MainScreen model={model} item={item} renderObject={renderObject} background={background} />
}

function MainScreen({ model, item, renderObject, background = false }: {
  model: ShellNavModel; item: RunNavItem; renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
  background?: boolean
}) {
  const act = model.activeEditor
  const activeHere = act && act.objName === item.obj_name ? act : null
  const activeMode = activeHere?.mode
  const activeTxn = activeHere?.txnId
  const refreshToken = model.refreshTokenFor(item.obj_name)
  const { requestEditor, editorsEnabled, setFullScreen, fullScreen } = model
  const editorValue = useMemo<ShellEditorMain | null>(() => (editorsEnabled ? {
    role: 'main',
    objName: item.obj_name,
    requestEditor,
    activeEditor: activeMode ? { mode: activeMode, txnId: activeTxn } : null,
    refreshToken,
  } : null), [editorsEnabled, item.obj_name, requestEditor, activeMode, activeTxn, refreshToken])
  const fs = useMemo(() => ({ on: fullScreen, set: setFullScreen }), [fullScreen, setFullScreen])
  // The header above has just named this object. A pane that also carries its
  // own heading (the transaction list, the document repository) reads that
  // from here and draws the name once — see ../../objectChrome.
  return (
    <ShellEditorProvider value={editorValue}>
      <ObjectChromeProvider objType={item.obj_type} objName={item.obj_name} fullScreen={fs}>
        {renderObject(item, { background })}
      </ObjectChromeProvider>
    </ShellEditorProvider>
  )
}

function SessionHost({ model, session, renderObject }: {
  model: ShellNavModel; session: EditorSession; renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
}) {
  const item = model.itemFor(session.objName)
  const visible = model.activeEditorId === session.id && model.current?.obj_name === session.objName
  const closeRequest = model.closeRequestFor(session.id)
  const { reportEditor, minimiseEditor, editorClosed, editorSaved, snapshotEditor } = model
  const lockTtlSeconds = model.config.editors.lockTtlSeconds
  const value = useMemo<ShellEditorSession>(() => ({
    role: 'session',
    objName: session.objName,
    session,
    visible,
    lockTtlSeconds,
    closeRequest,
    report: (p) => reportEditor(session.id, p),
    minimise: () => minimiseEditor(session.id),
    closed: () => editorClosed(session.id),
    saved: () => editorSaved(session.id),
    snapshot: (snap) => snapshotEditor(session.id, snap),
  }), [session, visible, lockTtlSeconds, closeRequest, reportEditor, minimiseEditor, editorClosed, editorSaved, snapshotEditor])
  if (!item) return null
  return (
    <ShellEditorProvider value={value}>
      {/* A different key per session: two editors on one screen are two
          independent instances. */}
      <div key={session.id} data-tw-shell-session={session.id}>{renderObject(item, { background: true })}</div>
    </ShellEditorProvider>
  )
}
